import { Exchange, Segment } from '@/types/symbols/exchangeTypes';

export const toNum = (v: string | number | null | undefined): number => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
        const n = Number(v.replace(/[,_\s]/g, ''));
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
};

export const formatToman = (n: number) => n.toLocaleString('fa-IR');

export const formatAmount = (n: number | string): string => {
    const val = typeof n === 'string' ? parseFloat(n) : n;
    if (!val || isNaN(val) || val === 0) return '0';
    if (val >= 1) return val.toLocaleString('fa-IR', { maximumFractionDigits: 3 });
    // For small decimals (< 1), format with up to 6 significant digits to avoid showing 0
    return val.toLocaleString('fa-IR', { maximumFractionDigits: 6 });
};

export const normSlug = (ex: Pick<Exchange, 'slug'> | { slug?: any } | any): string =>
    ex?.slug ? String(ex.slug).trim().toLowerCase() : '';

export const normalizeWsExchange = (raw: any): Exchange | null => {
    try {
        const cd = raw?.coinData || {};
        const rawSlug = raw?.slug ?? raw?._id ?? raw?.name;
        const slug = rawSlug ? String(rawSlug).trim().toLowerCase() : '';
        if (!slug) return null; // require a valid slug

        return {
            name: raw?.name ?? '',
            logo: raw?.logo ?? '',
            slug,
            registerLink: raw?.registerLink ?? '#',
            sponsored: !!raw?.sponsored,
            verified: !!raw?.verified,
            feeMax: Number(raw?.feeMax ?? 0),
            feeMin: Number(raw?.feeMin ?? 0),
            rating: typeof raw?.rating === 'number' ? raw.rating : Number(raw?.rating ?? 0),
            coinData: {
                buyPrice: String(cd.buyPrice ?? cd.buyPriceToman ?? ''),
                buyPriceToman: String(cd.buyPriceToman ?? cd.buyPrice ?? ''),
                sellPrice: String(cd.sellPrice ?? cd.sellPriceToman ?? ''),
                sellPriceToman: String(cd.sellPriceToman ?? cd.sellPrice ?? ''),
                sellAmount: String(cd.sellAmount ?? cd.sellAmountToman ?? ''),
                sellAmountToman: String(cd.sellAmountToman ?? cd.sellAmount ?? ''),
                buyAmount: String(cd.buyAmount ?? cd.buyAmountToman ?? ''),
                buyAmountToman: String(cd.buyAmountToman ?? cd.buyAmount ?? ''),
            },
        };
    } catch {
        return null;
    }
};

export const getPrice = (ex: Exchange, seg: Segment): number => {
    const cd = ex.coinData;
    return seg === 'buy'
        ? toNum(cd.sellPriceToman) || toNum(cd.sellPrice) // user buys → exchange SELL price
        : toNum(cd.buyPriceToman) || toNum(cd.buyPrice); // user sells → exchange BUY price
};

export const getAmount = (ex: Exchange, seg: Segment): number => {
    const cd = ex.coinData;
    return seg === 'buy'
        ? toNum(cd.buyAmount) || toNum(cd.buyAmountToman)
        : toNum(cd.sellAmount) || toNum(cd.sellAmountToman);
};

export const prepareSSR = (list: Exchange[] = []): Exchange[] => {
    const seen = new Set<string>();
    const cleaned: Exchange[] = [];
    for (const e of list) {
        const slug = normSlug(e);
        if (!slug) {
            console.warn('[Exchanges] Dropping item with empty slug:', e?.name);
            continue;
        }
        if (seen.has(slug)) {
            console.warn('[Exchanges] Dropping duplicate slug from SSR:', slug);
            continue;
        }
        seen.add(slug);
        cleaned.push({ ...e, slug });
    }
    return cleaned;
};
